import { HelpMessage } from 'utils/constants'
import { CIMSColumn } from '../../../CIMSColumn/types'
import { Divider, Space, Spin } from 'antd'
import CIMSLink from 'components/CIMSLink'
import { alphabeticalSort } from '@cims-online/shared'
import SharePointExternalLink from './components/SharePointExternalLink'
import PacketLink from './components/PacketLink'
import {
  UseExternalLinksOptions,
  UseSharePointExternalLinksOptions,
} from './types'

/**
 * hook to handle the external links column in CIMSTables
 *
 * @see {@link UseExternalLinksOptions}
 * @see {@link UseSharePointExternalLinksOptions}
 */
export function useExternalLinks<T extends object>(
  options: UseExternalLinksOptions<T> | UseSharePointExternalLinksOptions<T>
): CIMSColumn<T> {
  if (options.type === 'basic') {
    const { dataFn } = options
    return {
      title: 'External Links',
      width: 200,
      help: HelpMessage.ExternalLinks({ search: true }),
      render: (record) => {
        const { externalLinks, prominentLinks } = dataFn(record)
        return (
          <Space direction="vertical" size="small" style={{ maxWidth: 200 }}>
            {prominentLinks &&
              prominentLinks
                .sort(alphabeticalSort('label'))
                .map(({ label, ...link }, i) => {
                  const key = `${Math.random()}-prominentLinks-${i}`
                  return link.external === false ? (
                    <CIMSLink key={key} {...link}>
                      {label}
                    </CIMSLink>
                  ) : (
                    <CIMSLink key={key} external href={link.href}>
                      {label}
                    </CIMSLink>
                  )
                })}
            {(prominentLinks?.length ?? 0) > 0 && externalLinks.length > 0 && (
              <Divider style={{ margin: 0 }} />
            )}
            {externalLinks
              .sort(alphabeticalSort('label'))
              .map(({ href, label }, i) => (
                <CIMSLink
                  key={`${Math.random()}-externalLinks-${i}`}
                  external
                  href={href}
                >
                  {label}
                </CIMSLink>
              ))}
          </Space>
        )
      },
    }
  }
  const { dataFn, parent, loading: loadingProp } = options
  return {
    title: 'External Links',
    width: 200,
    help: HelpMessage.ExternalLinks({ search: true }),
    render: (record) => {
      const { externalLinks, folder, packet, resource, prominentLinks } =
        dataFn(record)
      return (
        <Spin size="small" spinning={loadingProp ?? false}>
          <Space direction="vertical" size="small" style={{ maxWidth: 200 }}>
            {resource && (
              <SharePointExternalLink {...resource} parent={parent} />
            )}
            {packet && <PacketLink {...packet} />}
            {prominentLinks &&
              prominentLinks
                .sort(alphabeticalSort('label'))
                .map(({ label, ...link }, i) => {
                  const key = `${folder}-prominentLinks-${i}`
                  return link.external === false ? (
                    <CIMSLink key={key} {...link}>
                      {label}
                    </CIMSLink>
                  ) : (
                    <CIMSLink key={key} external href={link.href}>
                      {label}
                    </CIMSLink>
                  )
                })}
            {externalLinks.length > 0 && <Divider style={{ margin: 0 }} />}
            {externalLinks
              .sort(alphabeticalSort('label'))
              .map(({ href, label }, i) => (
                <CIMSLink
                  key={`${folder}-externalLinks-${i}`}
                  external
                  href={href}
                >
                  {label}
                </CIMSLink>
              ))}
          </Space>
        </Spin>
      )
    },
    filter: {
      type: 'search',
      with: 'callback',
      onFilter: (value, record) => {
        const {
          externalLinks,
          resource,
          folder,
          packet,
          prominentLinks = [],
        } = dataFn(record)
        const lower = value.toString().toLowerCase()
        const bySharePoint = !!resource && 'sharepoint folder'.includes(lower)
        const byParent = parent.name.toLowerCase().includes(lower)
        const byFolder = folder.toLowerCase().includes(lower)
        const byPacketName =
          packet?.name?.toLowerCase().includes(lower) ?? false
        const byPacketUrl =
          packet?.webUrl?.toLowerCase().includes(lower) ?? false
        const byLinks = [...externalLinks, ...prominentLinks].some((link) => {
          const href = 'href' in link ? link.href : undefined
          return (
            href?.toLowerCase().includes(lower) ||
            link.label.toLowerCase().includes(lower)
          )
        })
        return (
          bySharePoint ||
          byParent ||
          byFolder ||
          byLinks ||
          byPacketName ||
          byPacketUrl
        )
      },
    },
  }
}
